/**
 * PRD Generation Status Panel Component
 *
 * Displays real-time status, progress, timer, and cancellation controls
 * for active PRD generation sessions.
 */

import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChipProps } from '@heroui/react';
import { Button, Card, CardBody, Chip } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  PauseCircle,
  X,
  XCircle,
} from 'lucide-react';

import { useElapsedTime } from '~/hooks/useElapsedTime';
import type { ProjectSessionStatusBatchItem } from '~/services/api/prd';
import { useSSEStore } from '~/stores/sseStore';
import { formatRelativeTime } from '~/utils/formatDuration';
import { logger } from '~/utils/logger';

import { ConfirmationDialog } from '~/components/ConfirmationDialog';

import type { SSEEvent } from '~/types/sse';

interface PRDGenerationStatusPanelProps {
  projectId: string;
  status: ProjectSessionStatusBatchItem;
  onCancel: () => Promise<void>;
  isCanceling: boolean;
  onDismiss: () => void;
  onResume: () => void;
}

/**
 * Status badge configuration for different generation states.
 */
interface StatusConfig {
  color: ChipProps['color'];
  icon: LucideIcon;
  label: string;
  pulse?: boolean;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  active: {
    color: 'primary',
    icon: Activity,
    label: 'Processing',
    pulse: false, // Spinner is on phase text instead
  },
  paused: {
    color: 'warning',
    icon: PauseCircle,
    label: 'Paused',
  },
  completed: {
    color: 'success',
    icon: CheckCircle2,
    label: 'Completed',
  },
  failed: {
    color: 'danger',
    icon: XCircle,
    label: 'Failed',
  },
  expired: {
    color: 'default',
    icon: Clock,
    label: 'Expired',
  },
};

/**
 * Format phase name for display.
 */
function formatPhaseName(phase: string | null): string {
  if (!phase) return 'Initializing...';

  // Map agent phases to user-friendly activity descriptions
  const phaseNames: Record<string, string> = {
    classifying: 'Classifying',
    gap_analysis: 'Gap detection',
    prd_generation: 'Generating',
    result_assembly: 'Finalizing',
    completed: 'Complete',
  };

  return phaseNames[phase] ?? phase.replace(/_/g, ' ');
}

/**
 * Get progress percentage based on current phase.
 * Phase 2 = 25%, Phase 3 = 50%, Phase 4 = 75%, Done = 100%
 *
 * Handles both SSE event names (analyzing, generating, assembling)
 * and database values (gap_analysis, prd_generation, result_assembly)
 */
function getPhaseProgress(phase: string | null): number {
  if (!phase) return 0;

  const phaseMap: Record<string, number> = {
    // Phase 1 - instant/hidden
    classifying: 0,
    classification: 0,

    // Phase 2 - gap analysis (25%)
    analyzing: 25, // SSE event name
    gap_analysis: 25, // Database value

    // Phase 3 - PRD generation (50%)
    generating: 50, // SSE event name
    prd_generation: 50, // Database value

    // Phase 4 - result assembly (75%)
    assembling: 75, // SSE event name
    result_assembly: 75, // Database value
    result_assembler: 75, // Alternative name

    // Done (100%)
    completed: 100,
  };

  return phaseMap[phase] ?? 0;
}

export function PRDGenerationStatusPanel(props: PRDGenerationStatusPanelProps): JSX.Element {
  const { isCanceling, onCancel, onDismiss, projectId, status } = props;
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Get status configuration
  const statusConfig = STATUS_CONFIG[status.status ?? 'active'] || STATUS_CONFIG['active'];
  const StatusIcon = statusConfig.icon;

  // Track elapsed time from server timestamps (survives refresh)
  const elapsedSecondsFromCreation = useElapsedTime(status.created_at);
  const elapsedSecondsFromUpdate = useElapsedTime(status.updated_at);

  // Get real-time progress from SSE store (if available)
  const projectEvents = useSSEStore(state => state.eventsByProject[projectId]);
  const latestPhaseEvent = projectEvents
    ?.slice()
    .reverse()
    .find((e: SSEEvent) => e.event === 'prd_phase');

  // Use SSE data if available (real-time), fallback to batch status
  const phaseLabel =
    latestPhaseEvent && 'phaseLabel' in latestPhaseEvent.data
      ? latestPhaseEvent.data.phaseLabel
      : formatPhaseName(status.generation_phase);

  // Extract phase name from SSE (if available) for real-time progress
  const currentPhaseName =
    latestPhaseEvent && 'phaseName' in latestPhaseEvent.data
      ? latestPhaseEvent.data.phaseName
      : status.generation_phase;

  // Calculate progress based on current phase (25%, 50%, 75%, 100%)
  // Prioritize SSE data for real-time updates
  const progress = getPhaseProgress(currentPhaseName);

  // Check if cancellable (active only)
  const isCancellable = status.status === 'active';

  // Listen for completion event and auto-navigate to review
  useEffect(() => {
    const completeEvent = projectEvents
      ?.slice()
      .reverse()
      .find((e: SSEEvent) => e.event === 'prd_assembling_complete');

    if (completeEvent) {
      logger.info(
        { component: 'PRDGenerationStatusPanel', projectId },
        'PRD generation completed (SSE event), navigating to review',
      );

      // Invalidate caches to refresh UI and wait for refetch
      const invalidateAndNavigate = async (): Promise<void> => {
        await queryClient.invalidateQueries({ queryKey: ['prd-session-status-batch'] });
        await queryClient.invalidateQueries({ queryKey: ['projects'] });

        // Wait 2 seconds to let users see the 100% completion state
        await new Promise(resolve => setTimeout(resolve, 2000));

        void navigate(`/projects/${projectId}/review`);
      };

      void invalidateAndNavigate();
    }
  }, [projectEvents, queryClient, projectId, navigate]);

  // Show panel only when session is active, paused, or recently completed/failed
  const shouldHide = !status.session_id || status.status === 'expired';

  if (shouldHide) {
    return <></>;
  }

  const handleCancelClick = (): void => {
    logger.info(
      { component: 'PRDGenerationStatusPanel', projectId, sessionId: status.session_id },
      'User initiated PRD generation cancellation',
    );
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = async (): Promise<void> => {
    try {
      logger.info(
        { component: 'PRDGenerationStatusPanel', projectId, sessionId: status.session_id },
        'Confirming PRD generation cancellation',
      );
      await onCancel();
      setShowCancelConfirm(false);
    } catch (error) {
      logger.error(
        { component: 'PRDGenerationStatusPanel', projectId },
        'Failed to cancel PRD generation',
        error instanceof Error ? error : new Error(String(error)),
      );
      // Error handling will be done in parent via react-query
    }
  };

  const handleCancelDialogClose = (): void => {
    setShowCancelConfirm(false);
  };

  return (
    <>
      <Card className="mt-3 bg-surface-elevated border-1 border-divider">
        <CardBody className="gap-3 p-4">
          {/* Header: Status Badge + Phase + Dismiss */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Chip
                color={statusConfig.color}
                size="sm"
                startContent={
                  <StatusIcon
                    className={`w-3.5 h-3.5 ${statusConfig.pulse ? 'animate-pulse' : ''}`}
                  />
                }
                variant="flat"
              >
                {statusConfig.label}
              </Chip>
              {status.status === 'active' && (
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary/10 dark:bg-primary/20">
                  <Loader2 className="w-3.5 h-3.5 text-primary dark:text-primary animate-spin" />
                  <span className="text-sm font-medium text-primary dark:text-primary">
                    {phaseLabel}...
                  </span>
                </div>
              )}
              {status.status === 'completed' && (
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-success/10 dark:bg-success/20">
                  <CheckCircle2 className="w-3.5 h-3.5 text-success dark:text-success" />
                  <span className="text-sm font-medium text-success dark:text-success">
                    Complete
                  </span>
                </div>
              )}
            </div>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={onDismiss}
              aria-label="Dismiss status panel"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Timer + Cancel Button */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted">
              {status.created_at ? (
                <>Started: {formatRelativeTime(elapsedSecondsFromCreation)}</>
              ) : status.updated_at ? (
                <>Updated: {formatRelativeTime(elapsedSecondsFromUpdate)}</>
              ) : (
                <>Initializing...</>
              )}
            </span>
            {isCancellable && (
              <Button
                color="danger"
                size="sm"
                variant="light"
                isLoading={isCanceling}
                isDisabled={isCanceling}
                onPress={handleCancelClick}
              >
                Cancel
              </Button>
            )}
          </div>

          {/* Progress Bar */}
          {(status.status === 'active' || status.status === 'paused') && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted">
                <span>Progress</span>
                <span>{progress}%</span>
              </div>
              <div className="bg-surface-elevated rounded-full h-2 overflow-hidden">
                <div
                  className="bg-primary rounded-full h-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Question Status (if available) */}
          {status.question_count > 0 && (
            <div className="flex items-center gap-4 text-sm">
              <div className="text-muted">
                Questions:{' '}
                <span className="text-foreground font-medium">
                  {status.answered_question_count}/{status.question_count} answered
                </span>
              </div>
            </div>
          )}

          {/* Error Message (for FAILED status) */}
          {status.status === 'failed' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-danger/10 border-1 border-danger/20">
              <AlertCircle className="w-5 h-5 text-danger shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-danger">
                <div className="font-semibold mb-1">Generation Failed</div>
                <div>
                  The PRD generation process encountered an error. Please try again or contact
                  support if the issue persists.
                </div>
              </div>
            </div>
          )}

          {/* Completed Message */}
          {status.status === 'completed' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-success/10 border-1 border-success/20">
              <CheckCircle2 className="w-5 h-5 text-success shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-success">
                <div className="font-semibold mb-1">PRD Generated Successfully</div>
                <div>Your PRD is ready to view and edit.</div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <ConfirmationDialog
          title="Cancel PRD Generation?"
          content={
            <div className="space-y-3">
              <p>Are you sure you want to cancel this PRD generation session?</p>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-warning/10 border-1 border-warning/20">
                <AlertCircle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
                <div className="text-sm text-warning">
                  <div className="font-semibold mb-1">⚠️ This will delete the session</div>
                  <p>Any progress will be lost and you'll need to start over.</p>
                </div>
              </div>
            </div>
          }
          isOpen={showCancelConfirm}
          isPending={isCanceling}
          onConfirm={() => void handleCancelConfirm()}
          onClose={handleCancelDialogClose}
          confirmText="Cancel Session"
          cancelText="Keep Running"
          confirmColor="danger"
        />
      )}
    </>
  );
}
